/**
 * Particle + shockwave FX for Jump. Obstacle passes spawn expanding rings and
 * spark bursts; landings kick up a dust puff; death and milestones get heavier
 * bursts. Additive blending keeps incoming hazards visible THROUGH the FX.
 */

import type { RenderQuality } from '../../rendering/renderQuality';
import type { JumpShockwaveSpec } from './jumpRenderTypes';
import { SHOCKWAVE_POOL_MAX, SHOCKWAVE_POOL_MAX_LOW } from './jumpRenderConstants';
import { drawGlowSprite } from './jumpGlowSprite';

export interface JumpParticle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  colour: string;
  /** Downward acceleration (CSS px/s²) — dust puffs settle; sparks float freely (0). */
  gravity: number;
}

export interface JumpParticleBurstSpec {
  x: number;
  y: number;
  colour: string;
  count: number;
  life: number;
  size: number;
  angleStart: number;
  angleEnd: number;
  speedMin: number;
  speedMax: number;
  gravity?: number;
}

export interface JumpShockwave {
  x: number;
  y: number;
  startR: number;
  endR: number;
  life: number;
  maxLife: number;
  thickness: number;
  colour: string;
  alpha: number;
  /** Vertical squash of the ring (1 = circle; <1 = a ground ripple ellipse). */
  flatten: number;
}

const MAX_PARTICLES = 256;

/** Peak opacity a particle reaches at birth (fades to 0 over its life). */
const PARTICLE_PEAK_ALPHA = 0.72;

/**
 * On-screen glow radius as a multiple of a particle's nominal `size`. The glow
 * sprite fades at its rim, so it's drawn a little larger than the old solid
 * disc (radius == size) to keep a comparable visual weight.
 */
const PARTICLE_GLOW_RADIUS_MUL = 2.2;

export class JumpFxSystem {
  /**
   * Fixed-capacity particle pool. All objects are allocated once up front and
   * recycled in place — the active particles occupy `[0, activeCount)` and dead
   * slots are swapped to the tail for reuse, so a burst (milestone / death /
   * dense pass spray) never allocates and never triggers a GC stutter mid-run.
   */
  private readonly particles: JumpParticle[] = [];
  private activeCount = 0;
  private readonly shockwaves: JumpShockwave[] = [];
  private readonly consumedShockwaves = new WeakSet<JumpShockwaveSpec>();
  private reducedMotion: boolean;
  private readonly particleHardCap: number;
  private readonly shockwaveHardCap: number;

  constructor(reducedMotion: boolean, quality: RenderQuality) {
    this.reducedMotion = reducedMotion;
    this.particleHardCap = Math.round(MAX_PARTICLES * quality.particleScale);
    this.shockwaveHardCap = quality.tier === 'low' ? SHOCKWAVE_POOL_MAX_LOW : SHOCKWAVE_POOL_MAX;
    for (let i = 0; i < this.particleHardCap; i += 1) {
      this.particles.push({
        x: 0,
        y: 0,
        vx: 0,
        vy: 0,
        life: 0,
        maxLife: 0,
        size: 0,
        colour: '#ffffff',
        gravity: 0,
      });
    }
  }

  private scaleCount(baseCount: number): number {
    if (this.particleHardCap >= MAX_PARTICLES) {
      return baseCount;
    }
    return Math.max(1, Math.round(baseCount * (this.particleHardCap / MAX_PARTICLES)));
  }

  emit(spec: JumpParticleBurstSpec): void {
    if (this.reducedMotion) {
      return;
    }
    const count = this.scaleCount(spec.count);
    const gravity = spec.gravity ?? 0;
    for (let i = 0; i < count; i += 1) {
      if (this.activeCount >= this.particleHardCap) {
        break;
      }
      const angle = spec.angleStart + (i / count) * (spec.angleEnd - spec.angleStart);
      const speed = spec.speedMin + Math.random() * (spec.speedMax - spec.speedMin);
      const p = this.particles[this.activeCount];
      p.x = spec.x;
      p.y = spec.y;
      p.vx = Math.cos(angle) * speed;
      p.vy = Math.sin(angle) * speed;
      p.life = spec.life;
      p.maxLife = spec.life;
      p.size = spec.size;
      p.colour = spec.colour;
      p.gravity = gravity;
      this.activeCount += 1;
    }
  }

  consumeShockwaveSpecs(specs: ReadonlyArray<JumpShockwaveSpec>): void {
    if (specs.length === 0 || this.reducedMotion) {
      return;
    }
    for (const spec of specs) {
      if (this.consumedShockwaves.has(spec)) {
        continue;
      }
      this.consumedShockwaves.add(spec);
      this.shockwaves.push({
        x: spec.x,
        y: spec.y,
        startR: spec.startR,
        endR: spec.endR,
        life: spec.life,
        maxLife: spec.life,
        thickness: spec.thickness,
        colour: spec.colour,
        alpha: spec.alpha,
        flatten: spec.flatten ?? 1,
      });
      if (this.shockwaves.length > this.shockwaveHardCap) {
        this.shockwaves.splice(0, this.shockwaves.length - this.shockwaveHardCap);
      }
    }
  }

  step(dt: number): void {
    // Active particles live in [0, activeCount); a dead one is swapped with the
    // last active slot so the pooled object is recycled (never freed). Order is
    // irrelevant for additive sparks, so swap-remove is safe.
    let i = 0;
    while (i < this.activeCount) {
      const p = this.particles[i];
      p.life -= dt;
      if (p.life <= 0) {
        this.activeCount -= 1;
        const recycled = this.particles[i];
        this.particles[i] = this.particles[this.activeCount];
        this.particles[this.activeCount] = recycled;
        continue;
      }
      p.vy += p.gravity * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vx *= 0.95;
      if (p.gravity === 0) {
        p.vy *= 0.95;
      }
      i += 1;
    }

    if (this.shockwaves.length > 0) {
      let write = 0;
      for (let read = 0; read < this.shockwaves.length; read += 1) {
        const w = this.shockwaves[read];
        w.life -= dt;
        if (w.life <= 0) {
          continue;
        }
        if (write !== read) {
          this.shockwaves[write] = w;
        }
        write += 1;
      }
      this.shockwaves.length = write;
    }
  }

  draw(ctx: CanvasRenderingContext2D, chromeShadows: boolean): void {
    this.drawShockwaves(ctx, chromeShadows);
    this.drawParticles(ctx);
  }

  private drawShockwaves(ctx: CanvasRenderingContext2D, chromeShadows: boolean): void {
    if (this.shockwaves.length === 0) {
      return;
    }
    ctx.save();
    for (const w of this.shockwaves) {
      const t = w.life / w.maxLife;
      const eased = 1 - t;
      const radius = w.startR + (w.endR - w.startR) * eased;
      ctx.globalAlpha = w.alpha * t * t;
      ctx.strokeStyle = w.colour;
      ctx.lineWidth = w.thickness * (0.6 + 0.4 * t);
      if (chromeShadows) {
        ctx.shadowColor = w.colour;
        ctx.shadowBlur = 12 * t;
      }
      ctx.beginPath();
      if (w.flatten === 1) {
        ctx.arc(w.x, w.y, radius, 0, Math.PI * 2);
      } else {
        ctx.ellipse(w.x, w.y, radius, radius * w.flatten, 0, 0, Math.PI * 2);
      }
      ctx.stroke();
      ctx.shadowBlur = 0;
    }
    ctx.restore();
  }

  private drawParticles(ctx: CanvasRenderingContext2D): void {
    if (this.activeCount === 0) {
      return;
    }
    // One cached glow sprite blitted per particle (additive) instead of a
    // per-particle path build + fill — a cheap downscaled `drawImage` that also
    // gives a softer spark. The sprite is shared/cached per colour.
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    for (let i = 0; i < this.activeCount; i += 1) {
      const p = this.particles[i];
      ctx.globalAlpha = Math.max(0, p.life / p.maxLife) * PARTICLE_PEAK_ALPHA;
      drawGlowSprite(ctx, p.colour, p.x, p.y, p.size * PARTICLE_GLOW_RADIUS_MUL);
    }
    ctx.restore();
  }

  reset(): void {
    this.activeCount = 0;
    this.shockwaves.length = 0;
  }
}
